import React, { useEffect, useRef } from 'react';

/**
 * [MOTION-B 2026-08-20] A figure that counts to its new value instead of jumping.
 *
 * Used for the money/KPI tiles (In Bank Summary, the Inventory KPI strip). When a save or a
 * background refresh moves a number, the change is easy to miss on a dense screen — a short
 * count draws the eye to exactly what moved, without a flashing highlight.
 *
 * RULES THIS FOLLOWS
 *   • NEVER animates on mount. Counting up from zero every time a tab opens would be a
 *     flourish, and it would fight the ~1s skeleton dwell that precedes it. Only a CHANGE to
 *     an already-displayed figure animates.
 *   • Writes `textContent` directly from the rAF loop instead of calling setState per frame.
 *     A KPI strip is 8-13 tiles; re-rendering the page 60 times a second for 400ms to move
 *     some digits is exactly the kind of "smoothness" that makes an app slower.
 *   • Interruptible: a second change mid-count continues from the value on screen rather than
 *     snapping back to the start.
 *   • Honours prefers-reduced-motion by setting the final value immediately.
 *
 * The JSX renders the CURRENTLY SHOWN value (a ref), not the incoming prop — otherwise a
 * re-render during a count would paint the final figure for one frame and the count would be
 * seen as a flash. `format` is applied every frame, so the tile keeps its exact styling
 * ($1,234.00 / -$0.00 collapsing / % suffixes — whatever the caller's formatter does).
 */
interface Props {
  value: number;
  format: (n: number) => string;
  /** Count duration. Kept at the `base` token; longer reads as a gimmick. */
  ms?: number;
}

const prefersReducedMotion = (): boolean => {
  try {
    return typeof window !== 'undefined' && !!window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
};

export const AnimatedNumber: React.FC<Props> = ({ value, format, ms = 400 }) => {
  const ref = useRef<HTMLSpanElement | null>(null);
  const shownRef = useRef<number>(value);
  const rafRef = useRef<number>(0);

  useEffect(() => {
    const el = ref.current;
    // Guard on the SHOWN value: `format` is often an inline arrow, so this effect can re-run
    // on any parent render. Without this, a count would restart on every unrelated render.
    if (!el || shownRef.current === value) return undefined;

    if (prefersReducedMotion() || !Number.isFinite(value) || !Number.isFinite(shownRef.current)) {
      shownRef.current = value;
      el.textContent = format(value);
      return undefined;
    }

    const from = shownRef.current;
    const to = value;
    const t0 = performance.now();
    cancelAnimationFrame(rafRef.current);

    const tick = (t: number) => {
      const p = Math.min(1, (t - t0) / ms);
      // easeOutCubic — quick start, soft settle; the same feel as `entrance`.
      const eased = 1 - Math.pow(1 - p, 3);
      const cur = from + (to - from) * eased;
      shownRef.current = p < 1 ? cur : to;
      el.textContent = format(shownRef.current);
      if (p < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [value, format, ms]);

  // Unmount safety: a tile removed mid-count (tab switch, filter change) must not leave a
  // frame callback writing into a detached node.
  useEffect(() => () => cancelAnimationFrame(rafRef.current), []);

  return <span ref={ref}>{format(shownRef.current)}</span>;
};

export default AnimatedNumber;
